import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Typography,
  Paper,
  Avatar,
  Divider,
  Fade,
  Zoom,
  CircularProgress,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const MotionPaper = motion(Paper);
const MotionBox = motion(Box);

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  isLoading?: boolean;
}

function ChatBox() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: 'Hello! How can I help you today?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (inputValue.trim() === '' || isLoading) return;

    const userQuestion = inputValue.trim();
    const newMessage: Message = {
      id: messages.length + 1,
      text: userQuestion,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages([...messages, newMessage]);
    setInputValue('');
    setIsLoading(true);

    // Add loading message
    const loadingMessage: Message = {
      id: messages.length + 2,
      text: 'Thinking...',
      sender: 'bot',
      timestamp: new Date(),
      isLoading: true,
    };
    setMessages((prev) => [...prev, loadingMessage]);

    try {
      // Call the API
      const response = await axios.post('https://dev-app3.arth.team/ask', {
        question: userQuestion,
        userId: 'test_user',
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Extract reply from response data
      const json = response.data;
      const reply =
        json.summary ||
        json.reply ||
        json.answer ||
        '(No answer returned)';

      // Add response time if available
      const responseText = json.responseTime
        ? `${reply}\n\n⚡ Response time: ${json.responseTime}ms`
        : reply;

      // Remove loading message and add actual response
      setMessages((prev) => {
        const filtered = prev.filter((msg) => !msg.isLoading);
        const botResponse: Message = {
          id: prev.length + 1,
          text: responseText,
          sender: 'bot',
          timestamp: new Date(),
        };
        return [...filtered, botResponse];
      });
    } catch (error: any) {
      // Remove loading message and add error response
      setMessages((prev) => {
        const filtered = prev.filter((msg) => !msg.isLoading);
        
        let errorText = '❌ Sorry, I encountered an error.';
        
        if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
          errorText = '❌ Error: Server is not running!\n💡 Please make sure the server is accessible.';
        } else if (error.response?.status === 500) {
          errorText = '❌ Server error occurred. Please try again later.';
        } else if (error.response?.data?.message) {
          errorText = `❌ Error: ${error.response.data.message}`;
        } else if (error.message) {
          errorText = `❌ Error: ${error.message}`;
        }
        
        const errorResponse: Message = {
          id: prev.length + 1,
          text: errorText,
          sender: 'bot',
          timestamp: new Date(),
        };
        return [...filtered, errorResponse];
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = () => {
    if (isOpen && !isMinimized) {
      setIsOpen(false);
    } else if (isMinimized) {
      setIsMinimized(false);
    } else {
      setIsOpen(true);
      setIsMinimized(false);
    }
  };

  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMinimized(true);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    setIsMinimized(false);
  };

  return (
    <>
      {/* Floating Action Button */}
      <AnimatePresence>
        {!isOpen && (
          <MotionBox
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 9999,
            }}
          >
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <IconButton
                onClick={handleToggle}
                sx={{
                  width: 64,
                  height: 64,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.6)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <ChatIcon sx={{ fontSize: 32 }} />
              </IconButton>
            </motion.div>
            {/* Pulse animation ring */}
            <motion.div
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.5, 0, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: '50%',
                border: '3px solid #667eea',
                pointerEvents: 'none',
              }}
            />
          </MotionBox>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <MotionPaper
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              height: isMinimized ? 60 : 500,
            }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            elevation={12}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              width: 380,
              maxWidth: 'calc(100vw - 48px)',
              borderRadius: 4,
              overflow: 'hidden',
              zIndex: 9999,
              background: 'linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: isMinimized ? 'pointer' : 'default',
              }}
              onClick={isMinimized ? handleToggle : undefined}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <BotIcon sx={{ fontSize: 28 }} />
                </motion.div>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    AI Assistant
                  </Typography>
                  <motion.div
                    initial={{ opacity: 0.6 }}
                    animate={{ opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Online
                    </Typography>
                  </motion.div>
                </Box>
              </Box>
              <Box display="flex" gap={0.5}>
                {!isMinimized && (
                  <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    <IconButton
                      size="small"
                      onClick={handleMinimize}
                      sx={{
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                      }}
                    >
                      <MinimizeIcon />
                    </IconButton>
                  </motion.div>
                )}
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                  <IconButton
                    size="small"
                    onClick={handleClose}
                    sx={{
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </motion.div>
              </Box>
            </Box>

            {/* Messages Area */}
            <AnimatePresence>
              {!isMinimized && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box
                    sx={{
                      height: 350,
                      overflowY: 'auto',
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '10px',
                        '&:hover': {
                          background: '#555',
                        },
                      },
                    }}
                  >
                    {messages.map((message, index) => (
                      <motion.div
                        key={message.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                            gap: 1.5,
                            alignItems: 'flex-start',
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              background:
                                message.sender === 'bot'
                                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            }}
                          >
                            {message.sender === 'bot' ? (
                              <BotIcon sx={{ fontSize: 18 }} />
                            ) : (
                              'U'
                            )}
                          </Avatar>
                          <Box
                            sx={{
                              maxWidth: '70%',
                              p: 1.5,
                              borderRadius: 2,
                              background:
                                message.sender === 'user'
                                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                  : 'white',
                              color: message.sender === 'user' ? 'white' : 'text.primary',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {message.isLoading && (
                              <CircularProgress size={16} sx={{ color: '#667eea' }} />
                            )}
                            <Box sx={{ flex: 1 }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {message.text}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  opacity: 0.7,
                                  mt: 0.5,
                                  display: 'block',
                                  fontSize: '0.7rem',
                                }}
                              >
                                {message.timestamp.toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </motion.div>
                    ))}
                    <div ref={messagesEndRef} />
                  </Box>

                  <Divider />

                  {/* Input Area */}
                  <Box sx={{ p: 2, background: 'white' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Type your message..."
                        value={inputValue}
                        disabled={isLoading}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !isLoading) {
                            handleSendMessage();
                          }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            '&:hover fieldset': {
                              borderColor: '#667eea',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#667eea',
                            },
                          },
                        }}
                      />
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <IconButton
                          onClick={handleSendMessage}
                          disabled={!inputValue.trim() || isLoading}
                          sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                            },
                            '&.Mui-disabled': {
                              background: '#e0e0e0',
                            },
                          }}
                        >
                          <SendIcon />
                        </IconButton>
                      </motion.div>
                    </Box>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </MotionPaper>
        )}
      </AnimatePresence>
    </>
  );
}

export default ChatBox;
